import { scheduleNotice } from '../../shared/schedule-notices';
import type { CalendarEvent, EventInput, PageBlock } from '../../shared/contracts';
import { eventHasEnded, formatEventRange, safeLink } from '../format';
import { LocationMap } from './LocationMap';
import { MapLinks } from './MapLinks';
import { SafeContent } from './SafeContent';
import './poster-tools.css';

function Block({ block }: { block: PageBlock }) {
  if (block.type === 'text') return <SafeContent body={block.text || ''} />;
  if (block.type === 'banner')
    return (
      <aside className="event-banner">
        <SafeContent body={block.text || ''} />
      </aside>
    );
  if (block.type === 'image') {
    const src = safeLink(block.url);
    return src ? (
      <figure className="event-image">
        <img src={src} alt={block.alt || ''} />
      </figure>
    ) : null;
  }
  const href = safeLink(block.url);
  return href ? (
    <a className="button event-page-button" href={href} rel="noreferrer">
      {block.label || 'Learn more'}
    </a>
  ) : null;
}

export function EventArticle({
  event,
}: {
  event: EventInput & Pick<CalendarEvent, 'scheduleChanges'>;
}) {
  const virtualUrl = safeLink(event.virtualLocation?.url);
  const posterUrl = safeLink(event.page.posterAsset?.url);
  const ended = event.status !== 'cancelled' && eventHasEnded(event);
  return (
    <article
      className="event-article"
      data-ended={ended || undefined}
      data-draft={event.status === 'draft' || undefined}
      data-layout={event.page.layout || 'simple'}
    >
      {event.page.heroUrl && safeLink(event.page.heroUrl) && (
        <img
          className="event-hero-image"
          src={safeLink(event.page.heroUrl)}
          alt={event.page.heroAlt || ''}
          style={{
            objectPosition: `${event.page.heroPosition?.x ?? 50}% ${event.page.heroPosition?.y ?? 50}%`,
          }}
        />
      )}
      <header className="event-hero">
        {event.status === 'draft' ? (
          <span className="event-ended-ribbon event-draft-ribbon">Draft</span>
        ) : (
          ended && <span className="event-ended-ribbon">Event ended</span>
        )}
        {event.status === 'draft' && ended && (
          <p className="draft-ended-notice">
            Event ended. Move its schedule into the future before publishing.
          </p>
        )}
        <span className="eyebrow">
          {event.status === 'cancelled' ? 'Cancelled' : 'Community event'}
        </span>
        <h1>{event.title}</h1>
        <time dateTime={event.start}>{formatEventRange(event)}</time>
      </header>
      {event.scheduleChanges?.map(scheduleNotice).map((notice, index) => (
        <aside className="schedule-notice" key={index}>
          <h2>{notice.label}</h2>
          <p>Previously: {notice.previous}</p>
          <p>Now: {notice.current}</p>
        </aside>
      ))}
      <section className="event-facts" aria-label="Event details">
        <div>
          <span className="fact-icon" aria-hidden="true">
            ◷
          </span>
          <p>
            <b>When</b>
            {formatEventRange(event)}
          </p>
        </div>
        {event.physicalLocation && (
          <div>
            <span className="fact-icon" aria-hidden="true">
              ⌖
            </span>
            <p>
              <b>{event.physicalLocation.name || 'In person'}</b>
              {event.physicalLocation.address}
              <small>{event.physicalLocation.details}</small>
              <MapLinks location={event.physicalLocation} />
            </p>
          </div>
        )}
        {event.virtualLocation && (
          <div>
            <span className="fact-icon" aria-hidden="true">
              ↗
            </span>
            <p>
              <b>Online</b>
              {event.virtualLocation.details || 'Join from your device'}
              {virtualUrl && (
                <a href={virtualUrl} rel="noreferrer">
                  {event.virtualLocation.label || 'Join online'}
                </a>
              )}
            </p>
          </div>
        )}
      </section>
      {event.physicalLocation && (
        <LocationMap
          key={JSON.stringify(event.physicalLocation)}
          location={event.physicalLocation}
        />
      )}
      {event.status === 'cancelled' && (
        <div className="cancelled-notice">
          This event has been cancelled. The details remain here for reference.
        </div>
      )}
      <div className="event-story">
        <p className="event-summary">{event.summary}</p>
        <SafeContent body={event.page.body} />
        {(event.page.blocks || []).map((block) => (
          <Block key={block.id} block={block} />
        ))}
      </div>
      {event.page.posterAsset && posterUrl && (
        <section className="poster-section">
          <h2>Event poster</h2>
          {event.page.posterAsset.type === 'image' ? (
            <img src={posterUrl} alt={event.page.posterAsset.alt} />
          ) : (
            <>
              <div className="pdf-preview">
                <span>PDF</span>
                <p>{event.page.posterAsset.name}</p>
              </div>
              <a className="button" href={posterUrl} target="_blank" rel="noreferrer">
                Download original poster (PDF)
              </a>
            </>
          )}
        </section>
      )}
    </article>
  );
}
